/**
 * SEO Engine Utility for TejCodex
 * Handles syllabus parsing, metadata extraction,
 * and JSON-LD generation.
 */

export const parseNoteHTML = async (
  folderCourse,
  formattedSubject,
  degree,
  course,
  semester,
  canonicalUrl
) => {

  try {

    // =========================
    // FETCH SYLLABUS FILE
    // =========================
    const syllabusFileName = folderCourse === "3YDC" 
      ? "LLB3YDCOUSyllabus.html" 
      : `${folderCourse}OUSyllabus.html`;

    const syllabusFile = `/Syllabus/${folderCourse}/${syllabusFileName}`;

    const response = await fetch(syllabusFile);

    const html =
      await response.text();

    const parser =
      new DOMParser();

    const doc =
      parser.parseFromString(
        html,
        "text/html"
      );

    // =========================
    // CLEAN FULL TEXT
    // =========================

    const fullText =
      doc.body.innerText.trim();

    // =========================
    // SUBJECT MATCHING
    // =========================

    const escapedSubject = formattedSubject
      .replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
      .replace(/\s+/g, "[\\s\\W-_]+");

    /**
     * Match subject block
     * until next "Paper"
     */

    const regex =
      new RegExp(
        `${escapedSubject}[\\s\\S]*?(?=Paper\\s+[IVX]+:|SEMESTER|$)`,
        "i"
      );

    const match =
      fullText.match(regex);

    const extractedText =
      match
        ? match[0]
        : "Syllabus content unavailable.";

    // =========================
    // EXTRACT UNITS
    // =========================

    const units =
      extractedText.match(
        /Unit\s*-\s*[IVX]+/gi
      ) || [];

    // =========================
    // SUMMARY
    // =========================

    const summary =
      extractedText
        .slice(0, 500)
        .trim();

    // =========================
    // FULL SNIPPET
    // =========================

    const fullSnippet =
      extractedText
        .slice(0, 2500)
        .trim();

    // =========================
    // FAQS
    // =========================

    const faqs = [
      {
        question:
          "What topics are covered in this subject?",
        answer:
          summary
      },
      {
        question:
          "Are these notes useful for semester exams?",
        answer:
          "Yes, these notes are structured according to the official university syllabus and examination pattern."
      },
      {
        question:
          "Does this subject contain unit-wise preparation material?",
        answer:
          "Yes, the material is organized unit-wise for easier semester preparation."
      }
    ];

    // =========================
    // BREADCRUMBS
    // =========================

    const breadcrumbs = [
      {
        name: "Home",
        path: "/"
      },
      {
        name: degree,
        path: `/notes/${degree}`
      },
      {
        name: course,
        path: `/notes/${degree}/${course}`
      },
      {
        name: `Semester ${semester}`,
        path:
          `/notes/${degree}/${course}/${semester}`
      },
      {
        name: formattedSubject,
        path: canonicalUrl
      }
    ];

    return {
      title: formattedSubject,
      summary,
      units,
      fullSnippet,
      faqs,
      breadcrumbs,
      syllabusText: extractedText
    };

  } catch (err) {

    console.error(
      "Syllabus parsing failed:",
      err
    );

    return null;
  }
};





/**
 * GENERATE JSON-LD
 */

export const generateJSONLD = (
  data,
  url
) => {

  const {
    title,
    summary,
    faqs,
    breadcrumbs
  } = data;

  // =========================
  // ARTICLE SCHEMA
  // =========================

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": summary,
    "url": url,

    "publisher": {
      "@type":
        "EducationalOrganization",

      "name": "TejCodex",

      "logo": {
        "@type": "ImageObject",

        "url":
          "https://tejcodex.com/favicon-512x512.png"
      }
    }
  };

  // =========================
  // BREADCRUMB SCHEMA
  // =========================

  const breadcrumbSchema = {
    "@context":
      "https://schema.org",

    "@type":
      "BreadcrumbList",

    "itemListElement":
      breadcrumbs.map(
        (b, i) => ({
          "@type": "ListItem",

          "position":
            i + 1,

          "name":
            b.name,

          "item": b.path.startsWith("http") ? b.path : `https://tejcodex.com${b.path}`
        })
      )
  };

  // =========================
  // FAQ SCHEMA
  // =========================

  const faqSchema = {
    "@context":
      "https://schema.org",

    "@type":
      "FAQPage",

    "mainEntity":
      faqs.map(f => ({
        "@type":
          "Question",

        "name":
          f.question,

        "acceptedAnswer": {
          "@type":
            "Answer",

          "text":
            f.answer
        }
      }))
  };

  return [
    articleSchema,
    breadcrumbSchema,
    faqSchema
  ];
};